import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "../styles/AuthPage.module.css";
import formStyles from "../styles/AuthForm.module.css";
import { auth, db } from "../../../firebase.js";
import {
  EmailAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  linkWithCredential,
  signInWithCredential,
  updateProfile,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  collection,
  query,
  limit,
  getDocs,
  orderBy,
} from "firebase/firestore";
import SocialAuth from "../components/SocialAuth";
import PhoneAuth from "../components/PhoneAuth";
import Tags from "../../../components/ui/display/Tags.jsx";
import GifteeFlowHeader from "../../giftees/components/GifteeFlowHeader.jsx";
import {
  AppleMonochromeIcon,
  GoogleMonochromeIcon,
  PhoneOutlineIcon,
} from "../components/AuthIcons";
import {
  ONBOARDING_STATUS_NEEDS_PROFILE,
  ONBOARDING_STATUS_PROFILE_COMPLETE,
  userNeedsNameAfterPhoneVerify,
  userNeedsProfileCompletion,
} from "../userOnboardingStatus.js";
import {
  captureGuestTreeForMigration,
  clearPendingGuestTree,
  flushPendingGuestTree,
  hasPendingGuestTree,
  isGuestUser,
  restoreGuestSessionFromPending,
  startGuestSession,
} from "../guestSession.js";
import {
  shouldEnterAppFromAuthLanding,
  shouldReturnGuestToAppOnAuthClose,
} from "../authLanding.js";
import { useAuth } from "../../../hooks/useAuth.jsx";

const PHONE_FLOW_REGISTER = "register";
const PHONE_FLOW_LOGIN = "login";

const STEP_START = "start";
const STEP_CREATE_ACCOUNT = "create_account";
/** Success + next steps — Figma 571:19333 (replaces legacy "How it works" wireframe) */
const STEP_POST_AUTH_WELCOME = "post_auth_welcome";

const POST_AUTH_WELCOME_STEPS = [
  {
    badge: "Step 1",
    title: "Add your loved ones",
    body: "Tell us about your friends and family, their special occasions, and what they love.",
  },
  {
    badge: "Step 2",
    title: "Get thoughtful gift ideas",
    body: "We'll suggest personalized gifts that match their interests and your budget.",
  },
  {
    badge: "Step 3",
    title: "Buy on time, every time",
    body: "Get reminders so you never miss an occasion and always have time to order.",
  },
];
const STEP_EMAIL_SIGN_IN = "email_sign_in";
const STEP_EMAIL_SIGN_UP = "email_sign_up";
const STEP_PHONE_VERIFY = "phone_verify";
/** After phone OTP — Figma 571:16944 (Add your name, step 2/2) */
const STEP_ADD_ACCOUNT_NAME = "add_account_name";

/** `?intent=` — in-app CTAs deep-link here without ending the guest session. */
const INTENT_CREATE_ACCOUNT = "create-account";
const INTENT_LOGIN = "login";

function guestStartErrorMessage(error) {
  if (
    error?.code === "auth/operation-not-allowed" ||
    error?.code === "auth/admin-restricted-operation"
  ) {
    return "Guest access is turned off for this Firebase project. Enable Authentication → Sign-in method → Anonymous, then try again.";
  }
  return error?.message || "Could not start your trial. Try again.";
}

export default function AuthPageScreen() {
  const navigate = useNavigate();
  const { currentUser, isGuest, phoneVerified, onboardingStatus } = useAuth();
  const resumeCheckedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const intentAppliedRef = useRef(false);

  const [step, setStep] = useState(STEP_START);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [postAuthDest, setPostAuthDest] = useState(null);
  const [phoneBackStep, setPhoneBackStep] = useState(STEP_START);
  /** `login` = Start → Login → phone (must exist in Auth); else phone signup / link flows */
  const [phoneFlowKind, setPhoneFlowKind] = useState(PHONE_FLOW_REGISTER);
  const [accountFirstName, setAccountFirstName] = useState("");
  const [accountLastName, setAccountLastName] = useState("");
  /** Headline on post-auth welcome — Figma headline is for new account; returning users get alternate copy */
  const [postAuthHeadline, setPostAuthHeadline] = useState("welcome_back");
  /** Phone field focused — compact chrome so form fits above iOS keyboard. */
  const [phoneFieldFocused, setPhoneFieldFocused] = useState(false);
  const accountNameScrollRef = useRef(null);
  const accountNameFormRef = useRef(null);

  const clearMessages = () => {
    setErr(null);
    setOk(null);
  };

  const shouldResumeAtNameStep = () =>
    Boolean(
      currentUser &&
        userNeedsNameAfterPhoneVerify(currentUser, phoneVerified),
    );

  /**
   * Guests already made a giftee while trialling, so conversion resolves to their
   * hub/home (null defers to `resolveDestination`) instead of the wizard.
   */
  const signupPostAuthDest = () => (isGuest ? null : "/create-giftee");

  const goToNameStepForIncompleteSignup = (message) => {
    setPhoneFlowKind(PHONE_FLOW_REGISTER);
    setPostAuthDest(signupPostAuthDest());
    setPhoneBackStep(STEP_CREATE_ACCOUNT);
    if (message) {
      setOk(message);
      setErr(null);
    }
    setStep(STEP_ADD_ACCOUNT_NAME);
  };

  /* Resume phone-first signups abandoned after OTP but before name entry. */
  useEffect(() => {
    if (resumeCheckedRef.current) return;
    if (phoneVerified === null || onboardingStatus === null) return;
    if (step !== STEP_START) return;
    if (!shouldResumeAtNameStep()) return;

    resumeCheckedRef.current = true;
    goToNameStepForIncompleteSignup(
      "Welcome back — finish setting up your account.",
    );
  }, [currentUser, phoneVerified, onboardingStatus, step]);

  /* Skip OTP when register flow re-enters phone verify with phone already linked. */
  useEffect(() => {
    if (step !== STEP_PHONE_VERIFY) return;
    if (phoneFlowKind !== PHONE_FLOW_REGISTER) return;
    if (!shouldResumeAtNameStep()) return;

    goToNameStepForIncompleteSignup(null);
  }, [step, phoneFlowKind, currentUser, phoneVerified, onboardingStatus]);

  /* Lock all scrolling on the name step and reset scroll whenever a name field
     is focused so iOS Safari cannot drift the fixed footer over the inputs. */
  useEffect(() => {
    if (step !== STEP_ADD_ACCOUNT_NAME) {
      return undefined;
    }

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;

    const pinScroll = () => {
      window.scrollTo(0, 0);
      html.scrollTop = 0;
      body.scrollTop = 0;
      if (accountNameScrollRef.current) {
        accountNameScrollRef.current.scrollTop = 0;
      }
    };

    const isAccountNameInput = (el) =>
      el instanceof HTMLInputElement &&
      el.closest("#account-name-form") != null;

    const isFirstAccountNameInput = (el) =>
      isAccountNameInput(el) && el.name === "given-name";

    const resetAccountNameShift = () => {
      accountNameFormRef.current?.style.setProperty("--account-name-shift", "0px");
    };

    /** Center the focused first-name shell in the area above the footer + keyboard. */
    const alignFirstAccountNameField = (input) => {
      const shell = input?.closest(`.${styles.accountNameInputShell}`);
      const form = accountNameFormRef.current;
      if (!shell || !form) return;

      const align = () => {
        pinScroll();

        const vv = window.visualViewport;
        const visibleTop = (vv?.offsetTop ?? 0) + 16;
        const visibleBottom = vv
          ? vv.offsetTop + vv.height
          : window.innerHeight;

        const footer = document.querySelector(`.${styles.accountNameFooter}`);
        const footerTop = footer?.getBoundingClientRect().top ?? visibleBottom;
        const safeBottom = Math.min(footerTop, visibleBottom) - 16;
        const safeTop = visibleTop;

        if (safeBottom <= safeTop) {
          resetAccountNameShift();
          return;
        }

        const shellRect = shell.getBoundingClientRect();
        const shellCenter = shellRect.top + shellRect.height / 2;
        const targetCenter = safeTop + (safeBottom - safeTop) / 2;
        const delta = shellCenter - targetCenter;

        if (Math.abs(delta) > 2) {
          form.style.setProperty("--account-name-shift", `${-delta}px`);
        } else {
          resetAccountNameShift();
        }
      };

      requestAnimationFrame(align);
      window.setTimeout(align, 50);
      window.setTimeout(align, 150);
      window.setTimeout(align, 350);
    };

    const handleFocusIn = (e) => {
      if (isFirstAccountNameInput(e.target)) {
        alignFirstAccountNameField(e.target);
        return;
      }
      if (isAccountNameInput(e.target)) {
        resetAccountNameShift();
        pinScroll();
      }
    };

    const handleViewportChange = () => {
      const active = document.activeElement;
      if (isFirstAccountNameInput(active)) {
        alignFirstAccountNameField(active);
        return;
      }
      pinScroll();
    };

    const preventTouchMove = (e) => {
      e.preventDefault();
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    pinScroll();

    document.addEventListener("focusin", handleFocusIn);
    window.addEventListener("scroll", pinScroll, true);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    document.addEventListener("touchmove", preventTouchMove, { passive: false });

    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("scroll", pinScroll, true);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      document.removeEventListener("touchmove", preventTouchMove, { passive: false });
      resetAccountNameShift();
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [step]);
  const goTo = (nextStep) => {
    clearMessages();
    setStep(nextStep);
  };

  const resolveDestination = async (uid) => {
    const gifteesRef = collection(db, "users", uid, "giftees");
    const ordered = query(gifteesRef, orderBy("createdAt", "desc"), limit(1));
    const orderedSnap = await getDocs(ordered);
    if (!orderedSnap.empty) {
      return "/home";
    }
    const anySnap = await getDocs(query(gifteesRef, limit(1)));
    if (anySnap.empty) return "/create-giftee";
    return "/home";
  };

  /**
   * Signs into a *different*, pre-existing account while a trial is in progress:
   * the giftees are snapshotted first, then re-created under the account the
   * visitor lands in (`flushPendingGuestTree`).
   */
  const signInToExistingAccount = async (signInFn) => {
    await captureGuestTreeForMigration();
    try {
      return await signInFn();
    } catch (error) {
      clearPendingGuestTree();
      throw error;
    }
  };

  const handleTryGiftGaff = async () => {
    clearMessages();
    setLoading(true);
    try {
      await startGuestSession();
      setPostAuthDest("/create-giftee");
      setPostAuthHeadline("guest_start");
      goTo(STEP_POST_AUTH_WELCOME);
    } catch (error) {
      console.error("Guest session start failed:", error);
      setErr(guestStartErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      const cred = await signInToExistingAccount(() =>
        signInWithEmailAndPassword(auth, email, password),
      );
      await flushPendingGuestTree(cred.user.uid);
      const userDocRef = doc(db, "users", cred.user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const phoneAlreadyVerified = userDocSnap.exists() && userDocSnap.data().phoneVerified;

      if (phoneAlreadyVerified) {
        setOk("Welcome back!");
        const dest = await resolveDestination(cred.user.uid);
        setPostAuthDest(dest);
        setPostAuthHeadline("welcome_back");
        goTo(STEP_POST_AUTH_WELCOME);
      } else {
        setOk("Signed in! Now verify your phone.");
        const dest = await resolveDestination(cred.user.uid);
        setPostAuthDest(dest);
        setPhoneFlowKind(PHONE_FLOW_REGISTER);
        setPhoneBackStep(STEP_EMAIL_SIGN_IN);
        goTo(STEP_PHONE_VERIFY);
      }
    } catch (error) {
      setErr(error.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setErr("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setErr("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const guestUser = isGuestUser(auth.currentUser) ? auth.currentUser : null;
    try {
      // Guests must *link* so the trial giftees stay on the same uid; creating a
      // new user would strand them under the abandoned anonymous account.
      const cred = guestUser
        ? await linkWithCredential(
            guestUser,
            EmailAuthProvider.credential(email, password),
          )
        : await createUserWithEmailAndPassword(auth, email, password);

      await setDoc(
        doc(db, "users", cred.user.uid),
        {
          email: cred.user.email,
          phoneNumber: null,
          authProviders: { email: true, google: false, phone: false },
          phoneVerified: false,
          onboardingStatus: ONBOARDING_STATUS_NEEDS_PROFILE,
          ...(guestUser ? {} : { createdAt: serverTimestamp() }),
          updatedAt: serverTimestamp(),
          lastSignInAt: serverTimestamp(),
        },
        { merge: true },
      );
      setOk("Account created! Now verify your phone.");
      setPostAuthDest(signupPostAuthDest());
      setPhoneFlowKind(PHONE_FLOW_REGISTER);
      setPhoneBackStep(STEP_EMAIL_SIGN_UP);
      goTo(STEP_PHONE_VERIFY);
    } catch (error) {
      const emailTaken =
        error?.code === "auth/email-already-in-use" ||
        error?.code === "auth/credential-already-in-use" ||
        error?.code === "auth/account-exists-with-different-credential";
      if (guestUser && emailTaken) {
        setErr(
          "That email already has an account. Use Login to sign in — your trial giftees will come with you.",
        );
      } else {
        setErr(error.message || "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSuccess = async (needsPhoneVerification) => {
    await flushPendingGuestTree(auth.currentUser?.uid);
    if (needsPhoneVerification) {
      setOk("Signed in! Now verify your phone.");
      setPhoneFlowKind(PHONE_FLOW_REGISTER);
      setPhoneBackStep(STEP_START);
      goTo(STEP_PHONE_VERIFY);
    } else {
      setOk("Welcome back!");
      const dest = await resolveDestination(auth.currentUser.uid);
      setPostAuthDest(dest);
      setPostAuthHeadline("welcome_back");
      goTo(STEP_POST_AUTH_WELCOME);
    }
  };

  const handlePhoneVerifySuccess = async () => {
    clearMessages();
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    await flushPendingGuestTree(uid);

    const userDocSnap = await getDoc(doc(db, "users", uid));
    const needsProfile = userNeedsProfileCompletion(userDocSnap.data());

    if (phoneFlowKind === PHONE_FLOW_LOGIN) {
      if (needsProfile) {
        goToNameStepForIncompleteSignup(null);
        return;
      }

      const dest = await resolveDestination(uid);
      if (dest === "/create-giftee") {
        navigate(dest, { state: { from: "/home" } });
      } else {
        navigate(dest);
      }
      return;
    }

    // Signup flow landed on a complete account (the phone already belonged to
    // one) — don't ask for a name that is already saved.
    if (!needsProfile) {
      const dest = await resolveDestination(uid);
      setPostAuthDest(dest);
      setPostAuthHeadline("welcome_back");
      goTo(STEP_POST_AUTH_WELCOME);
      return;
    }

    if (!postAuthDest) {
      const dest = await resolveDestination(uid);
      setPostAuthDest(dest);
    }
    goTo(STEP_ADD_ACCOUNT_NAME);
  };

  /**
   * The phone number already belongs to another account, so linking the trial to
   * it is impossible: sign into that account and bring the giftees along.
   */
  const handlePhoneCredentialInUse = async (credential) => {
    await signInToExistingAccount(() =>
      signInWithCredential(auth, credential),
    );
    return true;
  };

  /** Login verifies by signing in, which drops the guest uid — snapshot first. */
  const handleBeforePhoneSignIn = async () => {
    await captureGuestTreeForMigration();
  };

  /**
   * A login attempt can end signed out (unknown phone number), and an anonymous
   * session cannot be resumed — put the trial back so it isn't lost.
   */
  const handlePhoneAuthError = async (message) => {
    setErr(message ?? null);
    if (!message || auth.currentUser || !hasPendingGuestTree()) return;

    if (await restoreGuestSessionFromPending()) {
      setOk("Your trial giftees are still here.");
    }
  };

  const handleAccountNameSubmit = async (e) => {
    e.preventDefault();
    const fn = accountFirstName.trim();
    if (!fn) {
      setErr("Please enter your first name");
      return;
    }
    const ln = accountLastName.trim();
    clearMessages();
    setLoading(true);
    try {
      const displayName = [fn, ln].filter(Boolean).join(" ");
      const user = auth.currentUser;
      if (user) {
        await updateProfile(user, { displayName });
        await setDoc(
          doc(db, "users", user.uid),
          {
            firstName: fn,
            lastName: ln || null,
            displayName,
            isGuest: false,
            onboardingStatus: ONBOARDING_STATUS_PROFILE_COMPLETE,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      setOk("Welcome!");
      setPostAuthHeadline("account_created");
      goTo(STEP_POST_AUTH_WELCOME);
    } catch (error) {
      setErr(error.message || "Could not save your name");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccountPhone = () => {
    setPhoneFlowKind(PHONE_FLOW_REGISTER);
    setPostAuthDest(signupPostAuthDest());
    setPhoneBackStep(STEP_CREATE_ACCOUNT);
    if (shouldResumeAtNameStep()) {
      goToNameStepForIncompleteSignup(null);
      return;
    }
    goTo(STEP_PHONE_VERIFY);
  };

  const handleLoginWithPhone = () => {
    setPhoneFlowKind(PHONE_FLOW_LOGIN);
    setPostAuthDest(null);
    setPhoneBackStep(STEP_START);
    goTo(STEP_PHONE_VERIFY);
  };

  const hasConversionIntent = () =>
    Boolean(intentAppliedRef.current || searchParams.get("intent"));

  /**
   * In-app conversion (`/auth?intent=…`) closes back into the app. Login opened
   * from the start screen closes back to Try Gift Gaff / Login.
   */
  const handleCloseAuthFlow = (target = STEP_START) => {
    if (
      shouldReturnGuestToAppOnAuthClose({
        isGuest,
        targetIsStart: target === STEP_START,
        hasConversionIntent: hasConversionIntent(),
      })
    ) {
      navigate("/home");
      return;
    }
    goTo(target);
  };

  /* In-app CTAs (`/auth?intent=…`) open a flow directly, keeping the guest session. */
  useEffect(() => {
    if (intentAppliedRef.current) return;
    const intent = searchParams.get("intent");
    if (!intent) return;
    intentAppliedRef.current = true;

    if (intent === INTENT_CREATE_ACCOUNT) {
      setPhoneFlowKind(PHONE_FLOW_REGISTER);
      setPhoneBackStep(STEP_CREATE_ACCOUNT);
      setStep(STEP_CREATE_ACCOUNT);
      return;
    }

    if (intent === INTENT_LOGIN) {
      // The trial is snapshotted in `handleBeforePhoneSignIn`, right before the
      // Firebase user is swapped.
      handleLoginWithPhone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applied once per mount via intentAppliedRef
  }, [searchParams]);

  /* Account holders skip the start screen. Guests and signed-out visitors do not. */
  useEffect(() => {
    if (step !== STEP_START) return;
    if (
      !shouldEnterAppFromAuthLanding({
        isSignedIn: Boolean(currentUser),
        isGuest,
        profileComplete:
          onboardingStatus === ONBOARDING_STATUS_PROFILE_COMPLETE,
        hasConversionIntent: Boolean(
          intentAppliedRef.current || searchParams.get("intent"),
        ),
      })
    ) {
      return;
    }

    navigate("/home", { replace: true });
  }, [currentUser, isGuest, onboardingStatus, step, searchParams, navigate]);

  const phoneFlowTitle =
    phoneFlowKind === PHONE_FLOW_LOGIN ? "Login" : "New Account";

  const currentAuthUser = auth.currentUser;
  const phoneAlreadyOnAccount = Boolean(currentAuthUser?.phoneNumber);
  const phoneAuthMode =
    phoneFlowKind === PHONE_FLOW_LOGIN
      ? "signIn"
      : currentAuthUser && !phoneAlreadyOnAccount
        ? "link"
        : "signIn";

  if (step === STEP_START) {
    return (
      <main className={styles.pageMarketing}>
        <div className={styles.startScreen}>
          <div className={styles.startLogoWrap}>
            <img
              src="/images/GiftGaff_Wordmark_Dark.svg"
              alt="Gift Gaff"
              className={styles.startLogoImg}
            />
          </div>

          <div className={styles.startHero}>
            <p className={styles.startTitle}>
              Manage
              <br />
              thoughtful
              <br />
              gifting for all the
              <br />
              special people in
              <br />
              your life.
            </p>
            <img
              src="/images/GiftGaff_Logo.svg"
              alt=""
              aria-hidden="true"
              className={styles.startGift}
            />
          </div>

          <div className={styles.startButtons}>
            {err && <div className={formStyles.alertError}>{err}</div>}

            <button
              type="button"
              className={styles.startProviderBtn}
              disabled={loading}
              onClick={handleTryGiftGaff}
            >
              {loading ? "Starting\u2026" : "Try Gift Gaff"}
            </button>

            <button
              type="button"
              className={`${styles.startProviderBtn} ${styles.startProviderBtnGhost}`}
              disabled={loading}
              onClick={handleLoginWithPhone}
            >
              Login
            </button>
          </div>
        </div>
      </main>
    );
  }

  if (step === STEP_CREATE_ACCOUNT) {
    return (
      <main className={styles.pageFlow}>
        <div className={styles.screen}>
          <GifteeFlowHeader title=" " onClose={handleCloseAuthFlow} />
          <div className={styles.createAccountScreen}>
            <div className={styles.createAccountHero}>
              <h1 className={styles.createAccountTitle}>
                Create a new
                <br />
                account.
              </h1>
              <img
                src="/images/GiftGaff_Logo.svg"
                alt=""
                aria-hidden="true"
                className={styles.createAccountGift}
              />
            </div>

            <div className={styles.createAccountActions}>
              <button
                type="button"
                className={styles.createAccountProviderBtn}
                onClick={handleCreateAccountPhone}
              >
                <PhoneOutlineIcon color="var(--gg-midnight)" />
                Continue with Phone
              </button>

              <button
                type="button"
                className={styles.createAccountProviderBtn}
                onClick={() => {}}
              >
                <GoogleMonochromeIcon color="var(--gg-midnight)" />
                Continue with Google
              </button>

              <button
                type="button"
                className={styles.createAccountProviderBtn}
                onClick={() => {}}
              >
                <AppleMonochromeIcon color="var(--gg-midnight)" />
                Continue with Apple
              </button>
            </div>

            <p className={styles.createAccountTerms}>
              By creating an account or signing you agree to our{" "}
              <button
                type="button"
                className={styles.createAccountTermsLink}
                onClick={() => {}}
              >
                Terms and Conditions
              </button>
            </p>
          </div>
        </div>
      </main>
    );
  }

  if (step === STEP_POST_AUTH_WELCOME) {
    const hero =
      postAuthHeadline === "account_created" ? (
        <>
          Account&nbsp;created, time&nbsp;to
          <br />
          start&nbsp;gifting!
        </>
      ) : postAuthHeadline === "guest_start" ? (
        <>
          Welcome&nbsp;to Gift&nbsp;Gaff,
          <br />
          time&nbsp;to start&nbsp;gifting!
        </>
      ) : (
        <>
          Welcome&nbsp;back, time&nbsp;to
          <br />
          start&nbsp;gifting!
        </>
      );

    return (
      <main className={styles.postAuthWelcome}>
        <div className={styles.postAuthWelcomeInner}>
          <h1 className={styles.postAuthWelcomeHero}>{hero}</h1>

          <div className={styles.postAuthWelcomeSteps}>
            {POST_AUTH_WELCOME_STEPS.map((s) => (
              <section
                key={s.badge}
                className={styles.postAuthWelcomeStep}
                aria-label={s.title}
              >
                <Tags label={s.badge} color="midnight" variant="filled" />
                <h2 className={styles.postAuthWelcomeStepTitle}>{s.title}</h2>
                <p className={styles.postAuthWelcomeStepBody}>{s.body}</p>
              </section>
            ))}
          </div>

          <footer className={styles.postAuthWelcomeFooter}>
            <button
              type="button"
              className={styles.postAuthWelcomeCta}
              onClick={() => {
                const dest = postAuthDest || "/create-giftee";
                if (dest === "/create-giftee") {
                  navigate(dest, { state: { from: "/home" } });
                } else {
                  navigate(dest);
                }
              }}
            >
              Start Gifting
            </button>
          </footer>
        </div>
      </main>
    );
  }

  if (step === STEP_EMAIL_SIGN_IN) {
    return (
      <main className={styles.pageFlow}>
        <div className={styles.screen}>
          <GifteeFlowHeader title="Sign In" onClose={handleCloseAuthFlow} />
          <div className={styles.flowScroll}>
            <div className={styles.flowBody}>
              <form
                onSubmit={handleEmailSignIn}
                className={formStyles.form}
                noValidate
              >
                <InputField
                  id="signin-email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
                <PasswordField
                  id="signin-password"
                  label="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  show={showPw}
                  onToggle={() => setShowPw((v) => !v)}
                />

                {err && <div className={formStyles.alertError}>{err}</div>}
                {ok && <div className={formStyles.alertOk}>{ok}</div>}

                <button
                  type="submit"
                  className={formStyles.primaryBtn}
                  disabled={loading}
                >
                  {loading ? "Signing In\u2026" : "Continue"}
                </button>
              </form>

              <SocialAuth
                onSuccess={handleSocialSuccess}
                onError={(msg) => setErr(msg ?? null)}
                variant="inline"
              />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (step === STEP_EMAIL_SIGN_UP) {
    return (
      <main className={styles.pageFlow}>
        <div className={styles.screen}>
          <GifteeFlowHeader
            title="Create an Account"
            onClose={handleCloseAuthFlow}
          />
          <div className={styles.flowScroll}>
            <div className={styles.flowBody}>
              <form
                onSubmit={handleEmailSignUp}
                className={formStyles.form}
                noValidate
              >
                <InputField
                  id="signup-email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
                <PasswordField
                  id="signup-password"
                  label="Password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  show={showPw}
                  onToggle={() => setShowPw((v) => !v)}
                />
                <InputField
                  id="signup-confirm-password"
                  label="Confirm Password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                />

                {err && <div className={formStyles.alertError}>{err}</div>}
                {ok && <div className={formStyles.alertOk}>{ok}</div>}

                <button
                  type="submit"
                  className={formStyles.primaryBtn}
                  disabled={loading}
                >
                  {loading ? "Creating\u2026" : "Continue"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (step === STEP_PHONE_VERIFY) {
    return (
      <main
        className={`${styles.pageFlow} ${phoneFieldFocused ? styles.pageFlowKeyboardOpen : ""}`}
      >
        <div className={styles.screen}>
          <GifteeFlowHeader
            title={phoneFlowTitle}
            onClose={() => handleCloseAuthFlow(phoneBackStep)}
            className={
              phoneFieldFocused ? styles.flowHeaderKeyboardOpen : undefined
            }
          />
          <div className={`${styles.flowScroll} ${styles.flowScrollNoScroll}`}>
            <div className={`${styles.flowBody} ${styles.flowBodyPhone}`}>
              {err && <div className={formStyles.alertError}>{err}</div>}
              {ok && <div className={formStyles.alertOk}>{ok}</div>}
              <PhoneAuth
                onSuccess={handlePhoneVerifySuccess}
                onError={handlePhoneAuthError}
                onCredentialInUse={handlePhoneCredentialInUse}
                onBeforeSignIn={handleBeforePhoneSignIn}
                onPhoneFieldFocusChange={setPhoneFieldFocused}
                mode={phoneAuthMode}
                phoneEntry={
                  phoneFlowKind === PHONE_FLOW_LOGIN ? "login" : "register"
                }
              />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (step === STEP_ADD_ACCOUNT_NAME) {
    const showAccountNameContinue = accountFirstName.trim().length > 0;

    const pinAccountNameScroll = () => {
      window.scrollTo(0, 0);
      if (accountNameScrollRef.current) {
        accountNameScrollRef.current.scrollTop = 0;
      }
    };

    const handleAccountNameFieldFocus = (e) => {
      if (e.target.name === "given-name") {
        return;
      }
      accountNameFormRef.current?.style.setProperty("--account-name-shift", "0px");
      pinAccountNameScroll();
    };

    return (
      <main className={`${styles.pageFlow} ${styles.pageFlowAccountName}`}>
        <div className={styles.screen}>
          <GifteeFlowHeader
            title={phoneFlowTitle}
            onClose={() => handleCloseAuthFlow(phoneBackStep)}
            className={styles.accountNameHeader}
          />
          <div className={styles.accountNameLayout}>
            <div className={styles.accountNameScroll} ref={accountNameScrollRef}>
              <div className={styles.accountNameIntro}>
                <h2 className={styles.accountNameTitle}>Add your name</h2>
                <p className={styles.accountNameSubtitle}>
                  Add your name and your account
                  <br />
                  creation will be complete!
                </p>
              </div>
              <form
                id="account-name-form"
                ref={accountNameFormRef}
                className={styles.accountNameForm}
                onSubmit={handleAccountNameSubmit}
              >
                <p className={styles.accountNameFieldLabel}>Name</p>
                <div className={styles.accountNameInputShell}>
                  <input
                    className={styles.accountNameInput}
                    name="given-name"
                    placeholder="First name*"
                    value={accountFirstName}
                    onChange={(e) => setAccountFirstName(e.target.value)}
                    onFocus={handleAccountNameFieldFocus}
                    autoComplete="given-name"
                    aria-required="true"
                  />
                </div>
                <div className={styles.accountNameInputShell}>
                  <input
                    className={styles.accountNameInput}
                    name="family-name"
                    placeholder="Last Name"
                    value={accountLastName}
                    onChange={(e) => setAccountLastName(e.target.value)}
                    onFocus={handleAccountNameFieldFocus}
                    autoComplete="family-name"
                  />
                </div>
              </form>
              {err && (
                <div className={`${formStyles.alertError} ${styles.accountNameAlert}`}>
                  {err}
                </div>
              )}
            </div>
            <footer className={styles.accountNameFooter}>
              <div className={styles.accountNameFooterActions}>
                <button
                  type="button"
                  className={styles.accountNameBackBtn}
                  onClick={() => {
                    if (phoneAlreadyOnAccount || phoneVerified) {
                      goTo(STEP_CREATE_ACCOUNT);
                    } else {
                      goTo(STEP_PHONE_VERIFY);
                    }
                  }}
                  aria-label="Back"
                >
                  <i className="fas fa-arrow-left" aria-hidden="true" />
                </button>
                <button
                  type="submit"
                  form="account-name-form"
                  className={styles.accountNameSubmitBtn}
                  disabled={loading || !showAccountNameContinue}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span>
                    {loading ? "Saving\u2026" : "Create Account"}
                  </span>
                  <i
                    className={`fas fa-arrow-right ${styles.accountNameSubmitIcon}`}
                    aria-hidden="true"
                  />
                </button>
              </div>
              <div className={styles.accountNameProgress}>
                <div className={styles.accountNameProgressLabels}>
                  <span>2 / 2</span>
                  <span>NAME</span>
                </div>
                <div className={styles.accountNameProgressBar}>
                  <span className={styles.accountNameProgressFill} />
                  <span className={styles.accountNameProgressFill} />
                </div>
              </div>
            </footer>
          </div>
        </div>
      </main>
    );
  }

  return null;
}

function InputField({
  id,
  label,
  type = "text",
  autoComplete,
  value,
  onChange,
  placeholder,
}) {
  return (
    <div className={formStyles.inputGroup}>
      <label className={formStyles.inputLabel} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={formStyles.input}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
      />
    </div>
  );
}

function PasswordField({
  id,
  label,
  autoComplete,
  value,
  onChange,
  placeholder,
  show,
  onToggle,
}) {
  return (
    <div className={formStyles.inputGroup}>
      <label className={formStyles.inputLabel} htmlFor={id}>
        {label}
      </label>
      <div className={formStyles.inputRow}>
        <input
          id={id}
          className={formStyles.input}
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required
        />
        <button
          type="button"
          className={formStyles.eyeBtn}
          aria-label={show ? "Hide password" : "Show password"}
          onClick={onToggle}
        >
          <i className={show ? "fas fa-eye-slash" : "fas fa-eye"} />
        </button>
      </div>
    </div>
  );
}
